import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import TopMenu from "./TopMenu";

import { useTranslation } from "react-i18next";
import {
  Box,
  Flex,
  Heading,
  useDisclosure,
  Button,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
} from "@chakra-ui/react";
import { useToast } from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_BASE, DEFAULT_HEADERS } from "./services/apiClient";
import { getCurrentUserId } from "./services/authService";
import {
  describeAffiliation,
  normalizeSelectedThemes,
  stringifyList,
} from "./dashboard/utils/profileUtils";
import {
  FEED_FILTER_TYPES,
  FILTERABLE_TYPES,
} from "./dashboard/utils/filterStorage";
import { ensureAnalyticsSessionId } from "./dashboard/utils/analytics";
import { useDashboardFilters } from "./dashboard/hooks/useDashboardFilters";
import { useCachedProfile } from "./dashboard/hooks/useCachedProfile";
import { useConnections } from "./dashboard/hooks/useConnections";
import { useUserThreadMessageCounts } from "./dashboard/hooks/useUserThreadMessageCounts";
import { useSuggestedProfiles } from "./dashboard/hooks/useSuggestedProfiles";
import { useDashboardFeed } from "./dashboard/hooks/useDashboardFeed";
import { useLikes } from "./dashboard/hooks/useLikes";
import { PostComposer } from "./dashboard/components/PostComposer";
import { ProfileSidebar } from "./dashboard/components/ProfileSidebar";
import { FeedFilters } from "./dashboard/components/FeedFilters";
import { FeedList } from "./dashboard/components/FeedList";
import { RightRail } from "./dashboard/components/RightRail";
import NoticeForm from "./services/NoticeForm";

import { openExternalProfile } from "./services/openExternalProfile";
import { DashboardSurveyCard } from "./components/surveys/DashboardSurveyCard";
import { WeeklySummaryCard } from "./dashboard/components/WeeklySummaryCard";
import { getSurveyFlag } from "./config/surveyFlags";
import { ProfileCompletenessNudge } from "./components/surveys/ProfileCompletenessNudge";
import { useFeedActions } from "./dashboard/hooks/useFeedActions";
import FloatingStoryCard from "./components/StoryProposal";
import { ACCENT, SERIF } from "./theme/tokens";
import PageGuide from "./components/PageGuide";

const Dashboard = ({ onLogout }) => {
  const toast = useToast();
  const { t } = useTranslation("common");
  const translateRegion = useCallback(
    (region) => {
      if (!region) return region;

      switch (region) {
        case "North America":
          return t("reg_north_america");
        case "Latin America":
          return t("reg_latin_america");
        case "Africa and the Middle East":
          return t("reg_africa_me");
        case "Asia Pacific":
          return t("reg_asia_pacific");
        case "Western and Southern Europe":
          return t("reg_western_eu");
        case "Eastern Europe and Central Asia":
          return t("reg_eastern_eu");
        default:
          // se arriva qualcosa di non mappato, lascialo com'è
          return region;
      }
    },
    [t]
  );

  const cardBg = "surface.card";
  const cardText = "gray.800";
  const cardTitle = "gray.900";
  const cardMuted = "text.muted";
  const cardBorder = "border.default";
  const {
    isOpen: isNoticeFormOpen,
    onOpen: openNoticeForm,
    onClose: closeNoticeForm,
  } = useDisclosure();
  const { profile, user } = useCachedProfile();
  const localizedProfile = useMemo(() => {
    if (!profile) return profile;

    // copiamo il profilo e traduciamo solo le regioni
    const regions = Array.isArray(profile.regions)
      ? profile.regions.map((r) => translateRegion(r))
      : profile.regions
      ? translateRegion(profile.regions)
      : profile.regions;

    return {
      ...profile,
      regions,
    };
  }, [profile, translateRegion]);

  const resolvedUserId =
    getCurrentUserId() || user?.id || profile?.user?.id || profile?.id || null;
  const {
    threads: discussionThreads,
    loading: discussionThreadsLoading,
    error: discussionThreadsError,
  } = useUserThreadMessageCounts(resolvedUserId);
  const currentUserId = resolvedUserId;
  const latestProfiles = useSuggestedProfiles();
  const {
    connectionsCount,
    pendingConnectionsCount,
    connectedUserIds,
    connectedEmails,
  } = useConnections();
  const { selectedTypes, setSelectedTypes } = useDashboardFilters();
  const toggleAllFilters = useCallback(() => {
    setSelectedTypes((prev) => (prev.length > 0 ? [] : [...FEED_FILTER_TYPES]));
  }, [setSelectedTypes]);
  const [composerText, setComposerText] = useState("");
  const [postTags, setPostTags] = useState([]); // ["region:Latin America", "theme:7", ...]
  const toggleTag = useCallback((token) => {
    setPostTags((prev) =>
      prev.includes(token) ? prev.filter((x) => x !== token) : [...prev, token]
    );
  }, []);
  const clearTags = useCallback(() => setPostTags([]), []);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState("");
  const [mediaType, setMediaType] = useState(""); // "image" | "video" | ""
  const [latestNotices, setLatestNotices] = useState([]);
  const [gwbiEvents, setGwbiEvents] = useState([]); // internal GWBI events
  const [dmUnreadTotal, setDmUnreadTotal] = useState(0);

  const [visibleCount, setVisibleCount] = useState(20);
  const [refreshTick, setRefreshTick] = useState(0);
  const completeness = useMemo(() => {
    const p = profile || {};
    const hasPhoto = !!p.avatar_url;
    const hasHeadline = !!(p.headline && String(p.headline).trim());
    const hasBio = !!(p.biography && String(p.biography).trim());
    const hasOrg = !!(p.organization && String(p.organization).trim());
    const hasCountry = !!(p.country && String(p.country).trim());
    const hasRegion = Array.isArray(p.regions)
      ? p.regions.filter((r) => String(r).trim()).length > 0
      : !!(p.region && String(p.region).trim());
    const themesNorm = normalizeSelectedThemes(p.themes);
    const hasTheme = themesNorm.length > 0;
    const hasAcademicLink = !!(
      p.orcid_url ||
      p.google_scholar_url ||
      p.scholar_url ||
      p.researchgate_url ||
      p.research_gate_url ||
      p.rg_url ||
      p.academia_url
    );

    const checks = [
      { label: "Add a profile photo", ok: hasPhoto },
      { label: "Add a headline", ok: hasHeadline },
      { label: "Write a short bio", ok: hasBio },
      { label: "Add your organization", ok: hasOrg },
      { label: "Select your country", ok: hasCountry },
      { label: "Select at least one region", ok: hasRegion },
      { label: "Pick at least one theme", ok: hasTheme },
      {
        label: "Link an academic profile (ORCID/Scholar/RG/Academia)",
        ok: hasAcademicLink,
      },
    ];

    const filled = checks.filter((c) => c.ok).length;
    const pct = Math.round((filled / checks.length) * 100);
    const next = checks
      .filter((c) => !c.ok)
      .map((c) => c.label)
      .slice(0, 3);
    return { pct, next };
  }, [profile]);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const sessionIdRef = useRef(null);
  if (!sessionIdRef.current) {
    sessionIdRef.current = ensureAnalyticsSessionId();
  }
  const [posting, setPosting] = useState(false);
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const openComposer = () => setIsComposerOpen(true);
  const closeComposer = () => setIsComposerOpen(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { handleToggleLike, hydrateLikes } = useLikes(toast);
  const {
    conferences,
    combinedPosts,
    setCombinedPosts,
    myPostsCount,
    setMyPostsCount,
    myArticlesCount,
  } = useDashboardFeed({
    profile,
    user,
    locationPathname: location.pathname,
    onHydrateLikes: hydrateLikes,
    refreshKey: refreshTick,
  });

  const {
    submitPost,
    removePost,
    share: handleShare,
    shareModalOpen,
    setShareModalOpen,
    shareLink,
  } = useFeedActions({ profile, user, currentUserId, setCombinedPosts, toast });

  const resetComposer = () => {
    setComposerText("");
    setMediaFile(null);
    setMediaPreview("");
    setMediaType("");
    setPostTags([]);
  };

const viewExternalProfile = async (userId) => {
  try {
    await openExternalProfile(navigate, userId);
  } catch (e) {
    console.error("viewExternalProfile error:", e);
    alert("Could not open profile");
  }
};

  useEffect(() => {
    let cancelled = false;
    const fetchNotices = async () => {
      try {
        const res = await fetch(`${API_BASE}/notices?status=active`, {
          headers: DEFAULT_HEADERS,
        });
        const data = await res.json().catch(() => []);
        if (!res.ok) throw new Error("Failed to load notices");
        const list = Array.isArray(data) ? data : [];
        if (!cancelled) setLatestNotices(list.slice(0, 5));
      } catch (e) {
        if (!cancelled) setLatestNotices([]);
      }
    };
    fetchNotices();
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  useEffect(() => {
    let cancelled = false;
    const fetchGwbiEvents = async () => {
      try {
        const res = await fetch(`${API_BASE}/conferences_gwbi?limit=50`, {
          headers: DEFAULT_HEADERS,
        });
        const data = await res.json().catch(() => null);
        const list = Array.isArray(data)
          ? data
          : data?.items || data?.conferences || data?.events || [];
        if (!cancelled) setGwbiEvents(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) setGwbiEvents([]);
      }
    };
    fetchGwbiEvents();
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  useEffect(() => {
    if (!resolvedUserId) {
      setDmUnreadTotal(0);
      return;
    }
    const controller = new AbortController();
    let cancelled = false;

    const loadDmThreads = async () => {
      try {
        const token =
          localStorage.getItem("authToken") ||
          sessionStorage.getItem("authToken");
        const headers = {
          "Content-Type": "application/json",
          ...DEFAULT_HEADERS,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "X-User-Id": String(resolvedUserId),
        };
        const res = await fetch(
          `${API_BASE}/dm/threads?user_id=${encodeURIComponent(
            resolvedUserId
          )}`,
          { headers, signal: controller.signal }
        );
        if (!res.ok)
          throw new Error(`Failed to load DM threads (${res.status})`);
        const data = await res.json().catch(() => []);
        if (cancelled) return;
        const total = Array.isArray(data)
          ? data.reduce((sum, row) => {
              const value =
                row?.unread_count ??
                row?.unreadCount ??
                row?.unread_messages ??
                0;
              const num = Number(value);
              return sum + (Number.isFinite(num) ? num : 0);
            }, 0)
          : 0;
        setDmUnreadTotal(total);
      } catch (error) {
        if (!cancelled) setDmUnreadTotal(0);
      }
    };

    loadDmThreads();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [resolvedUserId]);

const handleViewOwnProfile = useCallback(() => {
  navigate("/profileextern");
}, [navigate]);



  const pickPhoto = () => photoInputRef.current?.click();
  const pickVideo = () => videoInputRef.current?.click();

  const onPhotoSelected = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setMediaFile(f);
    setMediaType("image");
    setMediaPreview(URL.createObjectURL(f));
  };
  const onVideoSelected = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setMediaFile(f);
    setMediaType("video");
    setMediaPreview(URL.createObjectURL(f));
  };

  const removeMedia = useCallback(() => {
    setMediaFile(null);
    setMediaPreview("");
    setMediaType("");
  }, []);

  const handleCreatePost = async () => {
    if (!composerText.trim() && !mediaFile) return;
    try {
      setPosting(true);
      await submitPost({
        text: composerText,
        mediaFile,
        mediaType,
        tags: postTags,
      });
      setMyPostsCount((c) => (Number.isFinite(c) ? c + 1 : 1));
      resetComposer();
      setIsComposerOpen(false);
    } catch (err) {
      console.error(err);
      alert(String(err.message || err));
    } finally {
      setPosting(false);
    }
  };

  // --- Optimistic handlers and UI wiring ---
  const [expandedArticles, setExpandedArticles] = useState(() => new Set());

  const toggleUploadedArticleAbstract = useCallback((id) => {
    if (!id) return;
    setExpandedArticles((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const triggerArticleDownload = useCallback((url) => {
    if (!url) return;
    if (typeof document === "undefined") {
      if (typeof window !== "undefined") {
        window.open(url, "_blank", "noopener,noreferrer");
      }
      return;
    }
    try {
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener,noreferrer";
      link.setAttribute("download", "");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      if (typeof window !== "undefined") {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    }
  }, []);

  // Apply card-type filter to the combined feed
  const filteredItems = Array.isArray(combinedPosts)
    ? combinedPosts.filter((item) => {
        if (!item || !item.type) return false;
        if (FILTERABLE_TYPES.has(item.type)) {
          return selectedTypes.includes(item.type);
        }
        // For non-filtered types (e.g., user posts), keep them visible
        return true;
      })
    : [];
  const visibleItems = filteredItems.slice(0, visibleCount);

  // Reset pagination when filters change
  useEffect(() => {
    setVisibleCount(20);
  }, [selectedTypes]);

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => prev + 20);
  }, []);

  // Measure the sticky header so the rails/filter bar can pin just beneath it.
  const headerRef = useRef(null);
  const [headerH, setHeaderH] = useState(0);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const update = () => setHeaderH(el.offsetHeight);
    update();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    if (ro) ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  return (
    <>
      <Box bg="surface.canvas" minH="100vh" color="gray.800">
        <Box
          ref={headerRef}
          position="sticky"
          top={0}
          zIndex={20}
          bg="surface.card"
        >
          <TopMenu onLogout={onLogout} />
        </Box>
        <Flex direction="row" align="flex-start" maxW="1600px" mx="auto">
          {/* Left rail — sticky beneath the header, scrolls within the viewport */}
          <Box
            as="aside"
            display={{ base: "none", md: "block" }}
            flex={{ base: "0 0 auto", md: "0 0 300px" }}
            w={{ md: "300px" }}
            position="sticky"
            top={`${headerH}px`}
            maxH={`calc(100vh - ${headerH}px)`}
            overflowY="auto"
            bg="surface.rail"
            p={4}
            borderRight="1px solid"
            borderColor="border.default"
          >
          <Box position="relative">
            <ProfileSidebar
              profile={localizedProfile}
              user={user}
              t={t}
              cardStyles={{
                  cardBg,
                  cardText,
                  cardTitle,
                  cardMuted,
                  cardBorder,
                }}
                completeness={completeness}
                stringifyList={stringifyList}
                connectionsCount={connectionsCount}
                pendingConnectionsCount={pendingConnectionsCount}
                dmUnreadTotal={dmUnreadTotal}
                myPostsCount={myPostsCount}
                myArticlesCount={myArticlesCount}
                discussionThreads={discussionThreads}
                discussionThreadsLoading={discussionThreadsLoading}
                discussionThreadsError={discussionThreadsError}
                onViewProfile={handleViewOwnProfile}
              />
            </Box>
            {getSurveyFlag("surveys.profile_nudge") && (
              <ProfileCompletenessNudge
                onStartSurvey={(code) => navigate(`/survey/${code}`)}
              />
            )}
          </Box>

          {/* Feed — the lead column; flows in the natural page scroll */}
          <Box
            as="main"
            flex="1 1 auto"
            w="100%"
            maxW="900px"
            minW={0}
            mx="auto"
            px={{ base: 4, md: 6 }}
            pb={10}
          >
            <Box pt={6} pb={5}>
              <Text
                color={ACCENT}
                fontSize="sm"
                fontWeight="semibold"
                letterSpacing="0.18em"
                textTransform="uppercase"
                mb={2}
              >
                {t("dashboard.eyebrow", "Your network")}
              </Text>
              <Flex align="center" gap={2}>
                <Heading
                  fontFamily={SERIF}
                  fontWeight="bold"
                  fontSize={{ base: "3xl", md: "4xl" }}
                  lineHeight="1.05"
                  color="gray.900"
                >
                  {profile?.first_name
                    ? t("dashboard.welcome_named", {
                        name: profile.first_name,
                        defaultValue: `Welcome back, ${profile.first_name}.`,
                      })
                    : t("dashboard.welcome", "Welcome back.")}
                </Heading>
                <PageGuide id="dashboard" autoOpenOnce size="md" />
              </Flex>
              <Text color="gray.600" mt={2} maxW="2xl">
                {t(
                  "dashboard.intro",
                  "The latest research, news and conversations from the global wine community."
                )}
              </Text>
            </Box>
            <Box
              position="sticky"
              top={`${headerH}px`}
              zIndex={2}
              bg="surface.canvas"
              pb={2}
              pt={2}
              borderBottom="1px solid"
              borderColor="border.default"
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
                gap={2}
              >
              </Box>
              <FeedFilters
                selectedTypes={selectedTypes}
                onChange={setSelectedTypes}
                onToggleAll={toggleAllFilters}
                t={t}/>
              <PostComposer
                profile={profile}
                t={t}
                composerText={composerText}
                onComposerTextChange={setComposerText}
                onOpenComposer={openComposer}
                onCloseComposer={closeComposer}
                isOpen={isComposerOpen}
                posting={posting}
                onSubmit={handleCreatePost}
                mediaFile={mediaFile}
                mediaPreview={mediaPreview}
                mediaType={mediaType}
                onPhotoTrigger={pickPhoto}
                onVideoTrigger={pickVideo}
                onPhotoSelected={onPhotoSelected}
                onVideoSelected={onVideoSelected}
                onRemoveMedia={removeMedia}
                photoInputRef={photoInputRef}
                videoInputRef={videoInputRef}
                tags={postTags}
                onToggleTag={toggleTag}
                onClearTags={clearTags}
              />
            </Box>

            <WeeklySummaryCard />

            {getSurveyFlag("surveys.dashboard_card") && (
              <Box mt={3} mb={3}>
                <DashboardSurveyCard
                  onStartSurvey={(code) => navigate(`/survey/${code}`)}
                />
              </Box>
            )}

            <Box mt={2}>
              <FeedList
                items={visibleItems}
                totalItems={filteredItems.length}
                t={t}
                onToggleLike={(item) =>
                  handleToggleLike(item, setCombinedPosts)
                }
                onDeletePost={removePost}
                currentUserId={currentUserId}
                expandedArticles={expandedArticles}
                onToggleArticle={toggleUploadedArticleAbstract}
                onDownloadArticle={triggerArticleDownload}
                onShare={handleShare}
              />
            </Box>
            {visibleItems.length < filteredItems.length && (
              <Box mt={4} textAlign="center">
                <Button onClick={loadMore} colorScheme="gwbi">
                  {t("dashboard.more") || "Load more"}
                </Button>
              </Box>
            )}
          </Box>

          {/* Right rail — sticky beneath the header */}
          <Box
            as="aside"
            display={{ base: "none", lg: "block" }}
            flex={{ base: "0 0 auto", lg: "0 0 300px" }}
            w={{ lg: "300px" }}
            position="sticky"
            top={`${headerH}px`}
            maxH={`calc(100vh - ${headerH}px)`}
            overflowY="auto"
            bg="surface.rail"
            p={4}
            borderLeft="1px solid"
            borderColor="border.default"
          >
          <RightRail
            cardBg={cardBg}
            cardText={cardText}
            cardTitle={cardTitle}
            cardMuted={cardMuted}
              latestProfiles={latestProfiles}
              connectedUserIds={connectedUserIds}
              connectedEmails={connectedEmails}
              describeAffiliation={describeAffiliation}
              viewExternalProfile={viewExternalProfile}
              user={user}
            profile={profile}
            t={t}
            conferences={conferences}
            gwbiEvents={gwbiEvents}
            notices={latestNotices}
            onAddNotice={openNoticeForm}
            onStartSurvey={(code) => navigate(`/survey/${code}`)}
            onCreateSurvey={() => navigate("/survey/new")}
            onManageSurveys={() => navigate("/surveys/manage")}
          />
        </Box>
      </Flex>
        <Modal isOpen={shareModalOpen} onClose={() => setShareModalOpen(false)} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{t("dashboard.share", "Share")}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text mb={2}>
                {t("dashboard.copy_link_prompt", "Copy this link to share the card:")}
              </Text>
              <Input value={shareLink} isReadOnly />
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme="gwbi"
                mr={3}
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(shareLink);
                    toast({
                      status: "success",
                      title: t("dashboard.link_copied", "Link copied"),
                    });
                    setShareModalOpen(false);
                  } catch (e) {
                    toast({
                      status: "error",
                      title: t("dashboard.copy_failed", "Could not copy link"),
                    });
                  }
                }}
              >
                {t("dashboard.copy_link", "Copy this link")}
              </Button>
              <Button variant="ghost" onClick={() => setShareModalOpen(false)}>
                {t("cancel")}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        <NoticeForm
          isOpen={isNoticeFormOpen}
          onClose={closeNoticeForm}
          initialNotice={null}
          onSaved={() => setRefreshTick((t) => t + 1)}
          currentUserId={currentUserId}
        />
        <FloatingStoryCard />
      </Box>
    </>
  );
};

export default Dashboard;
